import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getProdutos,
    registerUserSistemUENApi,
    createPixPaymentApi,
    createCardPaymentApi,
    cancelPixPaymentApi,
} from '../../../../Api';
import { styles } from '../../styles';
import { notify } from '../../../../components/Notify';
import { UEN_TOKEN_KEY, UEN_USER_KEY, UEN_PIX_KEY, getUENUser } from '../../../../utils';
import CartaoForm from '../../../../components/CartaoForm';
import FreteCalculator from '../../../../components/FreteCalculator';
import DadosCarteirinhaForm from '../../../../components/DadosCarteirinhaForm';

import { FiZap, FiCreditCard, FiArrowLeft, FiArrowRight } from 'react-icons/fi';

// 2 opções: Pix (fluxo próprio) ou Cartão (Card Payment Brick do MP, que internamente
// já oferece crédito + débito conforme o cartão e a configuração do Brick).
const PAYMENT_METHODS = [
    { id: 'pix', label: 'Pix', Icon: FiZap },
    { id: 'card', label: 'Cartão', Icon: FiCreditCard },
];

export default function Step3Products({ prev, setPix, data }) {
    const navigate = useNavigate();

    const [produtos, setProdutos] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingProdutos, setLoadingProdutos] = useState(true);
    const [erroProdutos, setErroProdutos] = useState(null);

    const [paymentMethod, setPaymentMethod] = useState('pix');
    const [paymentError, setPaymentError] = useState(null);
    // Bloqueio "uma compra por vez": guarda o id do Pix em aberto pra cancelar.
    const [pendingPixId, setPendingPixId] = useState(null);
    const [cancelandoPendente, setCancelandoPendente] = useState(false);
    // Dados da carteirinha coletados antes de pagar.
    const [dadosCarteirinha, setDadosCarteirinha] = useState(null);
    // Sub-etapa atual DENTRO do passo "produto": uma tela por vez (não empilha).
    // 'produto' → 'dados' → ('endereco' se física) → 'pagamento'.
    const [view, setView] = useState('produto');

    const montarDadosCadastro = () => ({
        ...(dadosCarteirinha || {}),
        ...(fretePronto?.endereco ? { endereco: fretePronto.endereco } : {}),
    });

    const capturarPendingPix = (err) => {
        if (err.response?.data?.pendingPix && err.response?.data?.pendingPixId) {
            setPendingPixId(String(err.response.data.pendingPixId));
        }
    };

    const handleCancelarPendente = async () => {
        if (cancelandoPendente || !pendingPixId) return;
        setCancelandoPendente(true);
        try {
            await cancelPixPaymentApi({ paymentId: pendingPixId });
            setPendingPixId(null);
            setPaymentError(null);
            notify('Pagamento em aberto cancelado. Pode comprar agora.', 'success');
        } catch {
            notify('Não foi possível cancelar o pagamento em aberto.', 'error');
        } finally {
            setCancelandoPendente(false);
        }
    };

    // Frete: produtos com frete=1 precisam que o cliente informe o CEP e
    // confirme o valor antes de qualquer pagamento. fretePronto = null
    // significa "aguardando o cliente preencher o CEP".
    const [fretePronto, setFretePronto] = useState(null);

    // Mantém o usuário registrado em ref para não disparar re-render do Brick
    // (o Brick é caro de remontar). O register acontece DENTRO do onSubmit do Brick.
    const registeredUserRef = useRef(getUENUser() || null);

    const carregarProdutos = (signal) => {
        setErroProdutos(null);
        setLoadingProdutos(true);
        return getProdutos({ signal })
            // Na registração o usuário é NOVO (sem carteirinha): só pode comprar
            // as opções "novas" — só digital e digital+física (digital=1). O
            // upgrade (digital=0, fisica=1) só faz sentido pra quem já tem digital.
            .then(res => setProdutos((res.data || []).filter(p => Number(p.digital) === 1)))
            .catch(err => {
                if (signal?.aborted) return;
                console.error("Erro ao carregar produtos:", err);
                setErroProdutos("Não foi possível carregar os produtos.");
            })
            .finally(() => {
                if (!signal?.aborted) setLoadingProdutos(false);
            });
    };

    useEffect(() => {
        const controller = new AbortController();
        carregarProdutos(controller.signal);
        return () => controller.abort();
    }, []);

    const valorTotal = useMemo(() => {
        if (!selected) return 0;
        return (Number(selected.price) || 0) + (fretePronto?.freteValor || 0);
    }, [selected, fretePronto]);

    const exigeFrete = selected?.fisica === 1; // física implica frete (calculado nos Correios)
    // Etapas: produto → dados → (endereço/frete se física) → pagamento.
    const dadosProntos = !!dadosCarteirinha;
    const enderecoPronto = !exigeFrete || !!fretePronto;
    const liberaPagamento = !!selected && dadosProntos && enderecoPronto;

    // Reseta dados/frete confirmados se o cliente trocar o produto selecionado.
    useEffect(() => { setFretePronto(null); setDadosCarteirinha(null); }, [selected?.id]);

    const ensureUserRegistered = async () => {
        const existingToken = localStorage.getItem(UEN_TOKEN_KEY);
        const existingUser = registeredUserRef.current || getUENUser();
        if (existingToken && existingUser?.id) {
            registeredUserRef.current = existingUser;
            return existingUser;
        }

        // O backend (registerUserSistemUEN) espera `phone`; o formulário guarda
        // o número em `data.telefone` — mapeia aqui pra não perder o telefone.
        const resUser = await registerUserSistemUENApi({ ...data, phone: data.telefone, productId: selected.id });
        const { token, user } = resUser.data;
        if (!token) throw new Error('Token não recebido pelo servidor.');
        localStorage.setItem(UEN_TOKEN_KEY, token);
        localStorage.setItem(UEN_USER_KEY, JSON.stringify(user));
        registeredUserRef.current = user;
        return user;
    };

    const handleGerarPix = async () => {
        if (!selected || loading) return;
        setPaymentError(null);
        setLoading(true);
        try {
            const user = await ensureUserRegistered();
            const resPix = await createPixPaymentApi({
                productId: selected.id,
                userId: user.id,
                cepDestino: fretePronto?.cepDestino,
                codigoServico: fretePronto?.codigoServico,
                dadosCadastro: montarDadosCadastro(),
            });
            setPix(resPix.data);
        } catch (err) {
            console.error('Erro no Pix:', err);
            capturarPendingPix(err);
            const msg = err.response?.data?.error
                || err.response?.data?.details
                || err.message
                || 'Erro ao gerar Pix.';
            setPaymentError(msg);
            notify(msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Checkout Transparente — back tokeniza, mandamos dados raw do cartão.
    const handleCardSubmit = async ({ token, paymentMethodId, issuerId, installments, paymentTypeId, billingAddress, phone }) => {
        setPaymentError(null);
        setLoading(true);

        // 1) Registro da conta — ACONTECE ANTES da cobrança. Se falhar aqui
        //    (ex.: email/CPF já cadastrado), a conta NÃO foi criada: mostramos
        //    o erro e ficamos no cadastro (não dá pra mandar pro dashboard).
        try {
            await ensureUserRegistered();
        } catch (err) {
            console.error('Erro ao registrar usuário no pagamento por cartão:', err);
            const msg = err.response?.data?.error
                || err.response?.data?.details
                || err.message
                || 'Não foi possível criar sua conta. Tente novamente.';
            setPaymentError(msg);
            notify(msg, 'error');
            setLoading(false);
            // Re-lança pro Brick rejeitar e RE-TOKENIZAR num novo "Pagar"
            // (token de cartão é de uso único).
            throw err;
        }

        // 2) Cobrança. A partir daqui a CONTA JÁ EXISTE. Em qualquer recusa
        //    (high_risk, saldo, etc.) informamos o cliente e o levamos pro
        //    /dashboard, de onde ele pode tentar pagar de novo. O toast
        //    sobrevive à navegação (NotifyComp fica fora do router).
        try {
            const deviceId = typeof window !== 'undefined' ? window.MP_DEVICE_SESSION_ID : undefined;
            const res = await createCardPaymentApi({
                productId: selected.id,
                token,
                paymentMethodId,
                issuerId,
                installments,
                paymentTypeId,
                deviceId,
                billingAddress,
                phone,
                cepDestino: fretePronto?.cepDestino,
                codigoServico: fretePronto?.codigoServico,
                dadosCadastro: montarDadosCadastro(),
            });

            if (res.data?.approved) {
                localStorage.removeItem(UEN_PIX_KEY);
                notify('Pagamento aprovado!', 'success');
                navigate('/minhacarteirinha');
                return;
            }

            // Recusado pelo MP (high_risk, saldo, etc.). A CONTA JÁ EXISTE.
            // Manda pro /produtos com o motivo — lá abre um modal recomendando Pix.
            const errorMsg = res.data?.error || 'Pagamento recusado.';
            const detailsMsg = res.data?.details;
            const motivo = detailsMsg && detailsMsg !== errorMsg ? `${errorMsg} ${detailsMsg}` : errorMsg;
            localStorage.removeItem(UEN_PIX_KEY);
            navigate('/produtos', { state: { cardRecusado: true, motivo } });
        } catch (err) {
            console.error('Erro no pagamento por cartão:', err);
            capturarPendingPix(err);
            const backendErr = err.response?.data?.error;
            const backendDetails = err.response?.data?.details;
            const msg = backendErr && backendDetails && backendDetails !== backendErr
                ? `${backendErr} ${backendDetails}`
                : (backendErr || backendDetails || err.message || 'Erro ao processar o pagamento.');

            // Pix pendente bloqueando a compra: mantém o cliente AQUI pra ele
            // cancelar o pendente (o redirect atrapalharia esse fluxo).
            if (err.response?.data?.pendingPix) {
                setPaymentError(msg);
                notify(msg, 'error');
                // Fica nesta tela (fluxo de cancelar o pendente) → re-lança pro
                // Brick re-tokenizar num novo "Pagar" (token de uso único).
                throw err;
            }

            // Demais erros na cobrança: a conta já existe → manda pro /produtos
            // com o motivo, onde abre o modal recomendando Pix.
            navigate('/produtos', { state: { cardRecusado: true, motivo: msg } });
        } finally {
            setLoading(false);
        }
    };

    if (loadingProdutos) {
        return <div style={{ color: '#64748B', textAlign: 'center', padding: '1rem' }}>Carregando produtos...</div>;
    }

    if (erroProdutos) {
        return (
            <div style={{ color: '#475569', textAlign: 'center' }}>
                <p>{erroProdutos}</p>
                <button onClick={() => carregarProdutos()} style={styles.secondaryButton}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (produtos.length === 0) {
        return <div style={{ color: '#64748B', textAlign: 'center', padding: '1rem' }}>Nenhum produto disponível no momento.</div>;
    }

    // Botão "Voltar" contextual: cada sub-etapa volta pra anterior; na primeira,
    // volta pro passo anterior do cadastro (prev).
    const voltarSubEtapa = () => {
        setPaymentError(null);
        if (view === 'pagamento') setView(exigeFrete ? 'endereco' : 'dados');
        else if (view === 'endereco') setView('dados');
        else if (view === 'dados') setView('produto');
        else prev();
    };

    const BotaoVoltar = () => (
        <button
            onClick={voltarSubEtapa}
            disabled={loading}
            style={{
                ...styles.secondaryButton,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                opacity: loading ? 0.6 : 1,
            }}
        >
            <FiArrowLeft size={16} /> Voltar
        </button>
    );

    return (
        <div style={{ position: 'relative' }}>
            {loading && (
                <div style={overlayStyles.backdrop}>
                    <div className="spinner" style={overlayStyles.spinner} />
                    <p style={{ color: '#fff', marginTop: '10px', fontWeight: 'bold' }}>
                        {paymentMethod === 'pix' ? 'Gerando Pix...' : 'Processando pagamento...'}
                    </p>
                </div>
            )}

            {/* ETAPA 1 — Escolha do produto */}
            {view === 'produto' && (
                <>
                    <h3 style={cardStyles.sectionTitle}>Escolha seu produto</h3>
                    {produtos.map(prod => (
                        <div
                            key={prod.id}
                            onClick={() => !loading && setSelected(prod)}
                            style={{
                                ...styles.productCard,
                                ...(selected?.id === prod.id ? styles.productCardSelected : {}),
                                opacity: loading ? 0.5 : 1,
                                cursor: loading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <span>{prod.title}</span>
                            <b style={{ color: '#059669' }}>R$ {Number(prod.price).toFixed(2)}</b>
                        </div>
                    ))}

                    <button
                        onClick={() => { if (selected) { setPaymentError(null); setView('dados'); } }}
                        disabled={!selected || loading}
                        style={{
                            ...styles.signUpButton,
                            marginTop: '1rem',
                            opacity: (!selected || loading) ? 0.6 : 1,
                            cursor: (!selected || loading) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        Continuar <FiArrowRight size={18} />
                    </button>
                    <BotaoVoltar />
                </>
            )}

            {/* ETAPA 2 — Dados da carteirinha */}
            {view === 'dados' && (
                <>
                    <h3 style={cardStyles.sectionTitle}>Dados da carteirinha</h3>
                    <DadosCarteirinhaForm
                        initial={dadosCarteirinha}
                        loading={loading}
                        textoBotao="Continuar"
                        onConfirmar={(d) => {
                            setDadosCarteirinha(d);
                            setPaymentError(null);
                            setView(exigeFrete ? 'endereco' : 'pagamento');
                        }}
                    />
                    <BotaoVoltar />
                </>
            )}

            {/* ETAPA 3 (só física) — Endereço de entrega + frete */}
            {view === 'endereco' && (
                <>
                    <h3 style={cardStyles.sectionTitle}>Endereço de entrega</h3>
                    {fretePronto ? (
                        <>
                            <div style={cardStyles.etapaOk}>
                                ✓ {fretePronto.modalidadeNome} — R$ {Number(fretePronto.freteValor).toFixed(2)}
                                <button type="button" onClick={() => setFretePronto(null)} style={cardStyles.editarLink} disabled={loading}>
                                    Editar
                                </button>
                            </div>
                            <button
                                onClick={() => { setPaymentError(null); setView('pagamento'); }}
                                disabled={loading}
                                style={{ ...styles.signUpButton, marginTop: '0.5rem', opacity: loading ? 0.6 : 1 }}
                            >
                                Continuar <FiArrowRight size={18} />
                            </button>
                        </>
                    ) : (
                        <FreteCalculator
                            produtoNome={selected.title}
                            produtoValor={Number(selected.price) || 0}
                            loading={loading}
                            onConfirmar={(dados) => { setFretePronto(dados); setPaymentError(null); setView('pagamento'); }}
                            onResetar={() => setFretePronto(null)}
                        />
                    )}
                    <BotaoVoltar />
                </>
            )}

            {/* ETAPA FINAL — Pagamento */}
            {view === 'pagamento' && liberaPagamento && (
                <>
                    <h3 style={cardStyles.sectionTitle}>Forma de pagamento</h3>
                    <div style={cardStyles.methodTabs}>
                        {PAYMENT_METHODS.map(m => {
                            const active = paymentMethod === m.id;
                            const Icon = m.Icon;
                            return (
                                <button
                                    key={m.id}
                                    type="button"
                                    disabled={loading}
                                    onClick={() => { setPaymentMethod(m.id); setPaymentError(null); }}
                                    style={{
                                        ...cardStyles.methodTab,
                                        ...(active ? cardStyles.methodTabActive : {}),
                                    }}
                                >
                                    <Icon size={20} />
                                    <span>{m.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    {paymentMethod === 'card' && (
                        <div style={cardStyles.brickWrapper}>
                            <CartaoForm
                                valor={valorTotal}
                                paymentTypeId="credit_card"
                                loading={loading}
                                onSubmit={handleCardSubmit}
                                maxInstallments={12}
                                userPhone={data?.telefone}
                                payerEmail={data?.email}
                            />
                        </div>
                    )}

                    {paymentMethod === 'pix' && (
                        <button
                            onClick={handleGerarPix}
                            disabled={loading}
                            style={{
                                ...styles.signUpButton,
                                background: loading ? '#94A3B8' : 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
                                boxShadow: loading ? 'none' : '0 4px 12px -2px rgba(16, 185, 129, 0.4)',
                                marginTop: '0.5rem',
                                cursor: loading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <FiZap size={18} /> {loading ? 'Aguarde...' : 'Gerar Pix'}
                        </button>
                    )}

                    {paymentError && (
                        <div style={cardStyles.errorBox}>
                            <strong>Pagamento não realizado.</strong>
                            <div style={{ marginTop: '4px' }}>{paymentError}</div>
                            {pendingPixId ? (
                                <button
                                    type="button"
                                    onClick={handleCancelarPendente}
                                    disabled={cancelandoPendente}
                                    style={{
                                        ...styles.secondaryButton,
                                        marginTop: '8px',
                                        color: '#9F1239',
                                        borderColor: '#FECDD3',
                                        opacity: cancelandoPendente ? 0.6 : 1,
                                    }}
                                >
                                    {cancelandoPendente ? 'Cancelando...' : 'Cancelar pagamento em aberto'}
                                </button>
                            ) : (
                                <div style={{ marginTop: '6px', fontSize: '0.8rem', opacity: 0.85 }}>
                                    Confira os dados e tente novamente.
                                </div>
                            )}
                        </div>
                    )}

                    {!loading && <BotaoVoltar />}
                </>
            )}

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

const overlayStyles = {
    backdrop: {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
        borderRadius: '12px',
        backdropFilter: 'blur(4px)',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid rgba(255, 255, 255, 0.2)',
        borderTop: '4px solid #10B981',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
};

const cardStyles = {
    sectionTitle: {
        fontSize: '0.95rem',
        fontWeight: 700,
        color: '#0F172A',
        margin: '1.5rem 0 0.75rem',
    },
    etapaOk: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10,
        padding: '0.6rem 0.85rem', margin: '1rem 0',
        fontSize: '0.85rem', fontWeight: 600, color: '#047857',
    },
    editarLink: {
        background: 'transparent', border: 'none', color: '#047857',
        fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
    },
    methodTabs: {
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1rem',
        flexWrap: 'wrap',
    },
    methodTab: {
        flex: '1 1 120px',
        padding: '0.9rem 0.75rem',
        borderRadius: '12px',
        border: '1.5px solid #E2E8F0',
        backgroundColor: '#FFFFFF',
        color: '#475569',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.4rem',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.85rem',
        transition: 'all 0.15s',
        fontFamily: 'inherit',
    },
    methodTabActive: {
        borderColor: '#059669',
        backgroundColor: '#ECFDF5',
        color: '#047857',
        boxShadow: '0 0 0 3px rgba(5, 150, 105, 0.1)',
    },
    brickWrapper: {
        backgroundColor: '#F8FAFC',
        padding: '1rem',
        borderRadius: '12px',
        border: '1px solid #E2E8F0',
        marginBottom: '0.5rem',
    },
    errorBox: {
        marginTop: '0.5rem',
        padding: '0.75rem 1rem',
        borderRadius: '10px',
        backgroundColor: '#FFF1F2',
        color: '#9F1239',
        border: '1px solid #FECDD3',
        fontSize: '0.875rem',
    },
};
